import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  RefreshCw, 
  TrendingUp, 
  TrendingDown,
  Target,
  Eye,
  ChevronDown,
  ChevronUp,
  Clock,
  Zap,
  BarChart3,
  Activity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════════════════════════════
// DESIGN SYSTEM - Colors & Styles
// ═══════════════════════════════════════════════════════════════════
const COLORS = {
  go: '#16E38A',
  wait: '#FACC15', 
  alert: '#F59E0B',
  watch: '#94A3B8',
  short: '#FF4D4F',
  long: '#16E38A'
};

// ═══════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

// L/S Ratio'yu görsel bar'a çevir (0-100 scale)
const getLSBarWidth = (ratio) => {
  if (!ratio) return 50;
  // 0.5 = %0 (extreme short), 1.0 = %50 (balanced), 3.0+ = %100 (extreme long)
  const normalized = Math.min(Math.max((ratio - 0.5) / 2.5, 0), 1) * 100;
  return normalized;
};

// L/S açıklaması
const getLSDescription = (ratio, action) => {
  if (!ratio) return { text: 'Veri yok', intensity: 'normal' };
  
  if (ratio >= 3.5) return { text: 'Kalabalık AŞIRI LONG', intensity: 'extreme' };
  if (ratio >= 2.5) return { text: 'Kalabalık yoğun LONG', intensity: 'high' };
  if (ratio >= 2.0) return { text: 'LONG ağırlıklı', intensity: 'medium' };
  if (ratio <= 0.5) return { text: 'Kalabalık AŞIRI SHORT', intensity: 'extreme' };
  if (ratio <= 0.67) return { text: 'SHORT ağırlıklı', intensity: 'medium' };
  return { text: 'Dengeli piyasa', intensity: 'normal' };
};

// Durum config
const getStatusConfig = (status) => {
  const configs = {
    'GO': { 
      label: 'GİR', 
      sublabel: 'Koşullar hazır',
      color: COLORS.go,
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/50',
      glow: 'shadow-[0_0_30px_rgba(22,227,138,0.2)]'
    },
    'ENTRY': { 
      label: 'GİR', 
      sublabel: 'Koşullar hazır',
      color: COLORS.go,
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/50',
      glow: 'shadow-[0_0_30px_rgba(22,227,138,0.2)]'
    },
    'WAIT': { 
      label: 'BEKLE', 
      sublabel: 'Teyit gerekli',
      color: COLORS.wait,
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/40',
      glow: ''
    },
    'SETUP': { 
      label: 'BEKLE', 
      sublabel: 'Setup aktif',
      color: COLORS.wait,
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/40',
      glow: ''
    },
    'ALERT': { 
      label: 'HAZIRLAN', 
      sublabel: 'Seviyeye yakın',
      color: COLORS.alert,
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/40',
      glow: ''
    },
    'WATCH': { 
      label: 'İZLE', 
      sublabel: 'Fırsat taranıyor',
      color: COLORS.watch,
      bg: 'bg-slate-500/10',
      border: 'border-slate-500/30',
      glow: ''
    }
  };
  return configs[status] || configs['WATCH'];
};

// Fiyat formatla
const formatPrice = (price) => {
  if (!price) return '-';
  if (price >= 1000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  return `$${price.toFixed(4)}`;
};

// Rejection score bar
const getScoreColor = (score) => {
  if (score >= 75) return 'bg-emerald-500';
  if (score >= 60) return 'bg-yellow-500';
  if (score >= 40) return 'bg-orange-500';
  return 'bg-red-500';
};

// ═══════════════════════════════════════════════════════════════════
// SIGNAL CARD COMPONENT - Professional Design
// ═══════════════════════════════════════════════════════════════════
const SignalCard = ({ symbol, symbolData }) => {
  const [expanded, setExpanded] = useState(false);
  
  const tradeStatus = symbolData?.trade_status || {};
  const status = tradeStatus.status || 'WATCH';
  const action = tradeStatus.action || 'SHORT';
  const keyLevel = symbolData?.key_level || {};
  const rejectionScore = tradeStatus.rejection_strength || tradeStatus.momentum_strength || 50;
  const lsRatio = symbolData?.long_short_ratio || 1;
  const fundingRate = symbolData?.funding_rate || 0;
  const setupType = tradeStatus.setup_type || 'trap'; // 'trap' or 'momentum_shift'
  const triggers = tradeStatus.triggers || [];
  
  const config = getStatusConfig(status);
  const lsInfo = getLSDescription(lsRatio, action);
  const lsBarWidth = getLSBarWidth(lsRatio);

  if (!['WATCH', 'ALERT', 'SETUP', 'WAIT', 'GO', 'ENTRY'].includes(status)) {
    return null;
  }

  return (
    <Card 
      className={`
        ${config.bg} ${config.border} ${config.glow}
        border rounded-2xl overflow-hidden
        transition-all duration-300 ease-out
        hover:translate-y-[-2px] hover:shadow-xl
      `}
      data-testid={`signal-card-${symbol}`}
    >
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-5 pb-4">
          {/* Setup Type Badge - Only for SETUP/GO/ENTRY */}
          {['SETUP', 'WAIT', 'GO', 'ENTRY'].includes(status) && (
            <div className="mb-3">
              <Badge 
                className={`
                  ${setupType === 'momentum_shift' 
                    ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' 
                    : 'bg-orange-500/20 text-orange-400 border-orange-500/30'}
                  border text-[10px] font-medium
                `}
              >
                {setupType === 'momentum_shift' ? '⚡ Momentum' : '🔥 Trap Setup'}
              </Badge>
            </div>
          )}
          
          {/* Top Row: Symbol + Direction */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span 
                className="text-2xl font-bold text-white tracking-tight"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                {symbol}
              </span>
              <Badge 
                className={`
                  ${action === 'SHORT' ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'}
                  border px-2.5 py-1 text-xs font-bold tracking-wider
                `}
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {action === 'SHORT' ? (
                  <><TrendingDown className="w-3 h-3 mr-1" />SATIŞ</>
                ) : (
                  <><TrendingUp className="w-3 h-3 mr-1" />ALIŞ</>
                )}
              </Badge>
            </div>
            
            {/* Status Badge - Large */}
            <div 
              className="px-4 py-2 rounded-full font-bold text-sm tracking-wide"
              style={{ 
                backgroundColor: `${config.color}20`,
                color: config.color,
                fontFamily: 'Chivo, sans-serif'
              }}
            >
              {config.label}
            </div>
          </div>

          {/* Status Description */}
          <p className="text-sm text-zinc-400 mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {config.sublabel}
          </p>

          {/* Crowd Sentiment Bar */}
          <div className="mb-5">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">Kalabalık Pozisyonu</span>
              <span 
                className={`text-xs font-medium ${
                  lsInfo.intensity === 'extreme' ? 'text-red-400' :
                  lsInfo.intensity === 'high' ? 'text-orange-400' : 'text-zinc-400'
                }`}
              >
                {lsInfo.text}
              </span>
            </div>
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden relative">
              <div 
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 via-yellow-500 to-red-500 opacity-30"
                style={{ width: '100%' }}
              />
              <div 
                className="absolute top-0 bottom-0 w-1 bg-white rounded-full transition-all duration-500"
                style={{ left: `calc(${lsBarWidth}% - 2px)` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-zinc-600">SATIŞ</span>
              <span className="text-[10px] text-zinc-600">ALIŞ</span>
            </div>
          </div>

          {/* Key Metrics Row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Price/Target */}
            <div className="bg-zinc-800/50 rounded-xl p-3">
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">
                {(status === 'GO' || status === 'ENTRY') ? 'Entry' : 'Kritik Seviye'}
              </span>
              <span 
                className="text-sm font-bold text-white"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {formatPrice(tradeStatus.entry || keyLevel.price)}
              </span>
            </div>

            {/* Distance */}
            <div className="bg-zinc-800/50 rounded-xl p-3">
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Uzaklık</span>
              <span 
                className={`text-sm font-bold ${
                  keyLevel.distance_pct <= 1.0 ? 'text-red-400' :
                  keyLevel.distance_pct <= 2.0 ? 'text-orange-400' : 'text-zinc-300'
                }`}
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                %{keyLevel.distance_pct?.toFixed(1) || '-'}
              </span>
            </div>
          </div>

          {/* Signal Quality Score - Only show for SETUP/WAIT/GO/ENTRY */}
          {['SETUP', 'WAIT', 'GO', 'ENTRY'].includes(status) && (tradeStatus.rejection_strength || tradeStatus.momentum_strength) && (
            <div className="mt-3 bg-zinc-800/50 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
                  {setupType === 'momentum_shift' ? 'Momentum Gücü' : 'Sinyal Kalitesi'}
                </span>
                <span 
                  className="text-xs text-zinc-400"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {tradeStatus.rejection_strength || tradeStatus.momentum_strength}/100
                </span>
              </div>
              <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${getScoreColor(tradeStatus.rejection_strength || tradeStatus.momentum_strength)} transition-all duration-500`}
                  style={{ width: `${tradeStatus.rejection_strength || tradeStatus.momentum_strength}%` }}
                />
              </div>
            </div>
          )}

          {/* L/S Intensity for WATCH/ALERT - Show how extreme the crowd is */}
          {['WATCH', 'ALERT'].includes(status) && (
            <div className="mt-3 bg-zinc-800/50 rounded-xl p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Uzun/Kısa</span>
                <span 
                  className={`text-sm font-bold ${
                    lsRatio >= 3.0 ? 'text-red-400' :
                    lsRatio >= 2.5 ? 'text-orange-400' :
                    lsRatio >= 2.0 ? 'text-amber-400' : 'text-zinc-300'
                  }`}
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {lsRatio.toFixed(2)}
                </span>
              </div>
              
              {/* Show momentum strength for WATCH if available */}
              {setupType === 'momentum_shift' && tradeStatus.momentum_strength && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Momentum Gücü</span>
                    <span 
                      className="text-xs text-zinc-400"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {tradeStatus.momentum_strength}/100
                    </span>
                  </div>
                  <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getScoreColor(tradeStatus.momentum_strength)} transition-all duration-500`}
                      style={{ width: `${tradeStatus.momentum_strength}%` }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Expand Button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full py-3 px-5 bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors flex items-center justify-center gap-2 text-zinc-400 text-sm"
          data-testid={`expand-btn-${symbol}`}
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              <span>Detayları Gizle</span>
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              <span>Analiz</span>
            </>
          )}
        </button>

        {/* Expanded Content */}
        {expanded && (
          <div className="px-5 pb-5 pt-2 border-t border-zinc-800/50 space-y-4 animate-in slide-in-from-top-2 duration-200">
            {/* Sentiment Data */}
            <div>
              <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <BarChart3 className="w-3 h-3" />
                Piyasa Verileri
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex justify-between items-center py-2 px-3 bg-zinc-800/30 rounded-lg">
                  <span className="text-xs text-zinc-500">Uzun/Kısa</span>
                  <span 
                    className={`text-sm font-bold ${
                      lsRatio >= 3.0 ? 'text-red-400' :
                      lsRatio >= 2.0 ? 'text-orange-400' : 'text-zinc-300'
                    }`}
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {lsRatio.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 px-3 bg-zinc-800/30 rounded-lg">
                  <span className="text-xs text-zinc-500">Fonlama</span>
                  <span 
                    className={`text-sm ${
                      fundingRate > 0.0005 ? 'text-red-400' :
                      fundingRate < -0.0005 ? 'text-emerald-400' : 'text-zinc-400'
                    }`}
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {(fundingRate * 100).toFixed(4)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Key Level */}
            <div>
              <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Target className="w-3 h-3" />
                Kritik Seviye
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="py-2 px-3 bg-zinc-800/30 rounded-lg">
                  <span className="text-[10px] text-zinc-500 block">Tür</span>
                  <span className="text-xs text-zinc-300">{keyLevel.name?.replace('_', ' ').replace('high', 'tepe').replace('low', 'dip').replace('24h', '24s') || '-'}</span>
                </div>
                <div className="py-2 px-3 bg-zinc-800/30 rounded-lg">
                  <span className="text-[10px] text-zinc-500 block">Fiyat</span>
                  <span 
                    className="text-sm text-amber-400 font-bold"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {formatPrice(keyLevel.price)}
                  </span>
                </div>
              </div>
            </div>

            {/* Trade Setup - Only for GO/SETUP */}
            {(status === 'GO' || status === 'ENTRY' || status === 'SETUP' || status === 'WAIT') && tradeStatus.entry && (
              <div>
                <h4 className="text-xs text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Zap className="w-3 h-3" />
                  İşlem Bilgileri
                </h4>
                <div className="grid grid-cols-4 gap-2">
                  <div className="py-2 px-3 bg-zinc-800/30 rounded-lg text-center">
                    <span className="text-[10px] text-zinc-500 block">Giriş</span>
                    <span className="text-xs text-emerald-400 font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatPrice(tradeStatus.entry)}
                    </span>
                  </div>
                  <div className="py-2 px-3 bg-zinc-800/30 rounded-lg text-center">
                    <span className="text-[10px] text-zinc-500 block">Zarar Kes</span>
                    <span className="text-xs text-red-400 font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatPrice(tradeStatus.stop_loss)}
                    </span>
                  </div>
                  <div className="py-2 px-3 bg-zinc-800/30 rounded-lg text-center">
                    <span className="text-[10px] text-zinc-500 block">Hedef</span>
                    <span className="text-xs text-blue-400 font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatPrice(tradeStatus.tp1)}
                    </span>
                  </div>
                  <div className="py-2 px-3 bg-zinc-800/30 rounded-lg text-center">
                    <span className="text-[10px] text-zinc-500 block">R/K</span>
                    <span className="text-xs text-amber-400 font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      1:{tradeStatus.rr_ratio || '-'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Current Price */}
            <div className="flex items-center justify-between py-2 px-3 bg-zinc-800/30 rounded-lg">
              <span className="text-xs text-zinc-500">Anlık Fiyat</span>
              <span 
                className="text-sm text-white font-bold"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {formatPrice(symbolData?.current_price)}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const AdminContrarianDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [viewMode, setViewMode] = useState('simple'); // 'simple' | 'pro'

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dataRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/contrarian/data`, { headers, timeout: 120000 })
      ]);
      
      setData(dataRes.data?.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Contrarian data fetch error:', error);
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-2 border-emerald-500/30 border-t-emerald-500 animate-spin" />
          <p className="text-zinc-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Radar taranıyor...</p>
        </div>
      </div>
    );
  }

  const symbols = data?.symbols || {};

  // Sinyalleri kategorize et
  const categorizedSignals = Object.entries(symbols)
    .filter(([_, d]) => d?.trade_status?.status && ['WATCH', 'ALERT', 'SETUP', 'WAIT', 'GO', 'ENTRY'].includes(d.trade_status.status))
    .reduce((acc, [symbol, data]) => {
      const status = data?.trade_status?.status;
      if (['GO', 'ENTRY'].includes(status)) acc.go.push([symbol, data]);
      else if (['WAIT', 'SETUP'].includes(status)) acc.wait.push([symbol, data]);
      else if (status === 'ALERT') acc.alert.push([symbol, data]);
      else if (status === 'WATCH') acc.watch.push([symbol, data]);
      return acc;
    }, { go: [], wait: [], alert: [], watch: [] });

  const totalSignals = Object.values(categorizedSignals).flat().length;

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Noise Texture Overlay */}
      <div className="fixed inset-0 opacity-[0.015] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]" />
      
      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* ═══════════════════════════════════════════════════════════════════
            HEADER
        ═══════════════════════════════════════════════════════════════════ */}
        <header className="mb-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-emerald-400" />
                </div>
                <h1 
                  className="text-3xl font-black text-white tracking-tight"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  Contrarian Radar
                </h1>
              </div>
              <p 
                className="text-zinc-500 text-sm"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Kalabalığın tersine · Akıllı trade fırsatları
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Mode Toggle */}
              <div className="bg-zinc-800/50 p-1 rounded-xl flex">
                <button
                  onClick={() => setViewMode('simple')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'simple' 
                      ? 'bg-emerald-500 text-white' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="mode-simple"
                >
                  Simple
                </button>
                <button
                  onClick={() => setViewMode('pro')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'pro' 
                      ? 'bg-emerald-500 text-white' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="mode-pro"
                >
                  Pro
                </button>
              </div>

              {/* Refresh */}
              <Button 
                onClick={handleRefresh} 
                disabled={refreshing}
                variant="outline"
                size="sm"
                className="border-zinc-700 hover:bg-zinc-800 hover:border-zinc-600"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Yenile
              </Button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mt-6 flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-zinc-400">{totalSignals} aktif sinyal</span>
            </div>
            {lastUpdate && (
              <div className="flex items-center gap-2 text-zinc-500">
                <Clock className="w-3 h-3" />
                <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {lastUpdate.toLocaleTimeString('tr-TR')}
                </span>
              </div>
            )}
          </div>
        </header>

        {/* ═══════════════════════════════════════════════════════════════════
            SIMPLE MODE - Signal Cards
        ═══════════════════════════════════════════════════════════════════ */}
        {viewMode === 'simple' && (
          <div className="space-y-10">
            {/* GO Signals */}
            {categorizedSignals.go.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                  <h2 
                    className="text-lg font-bold text-white"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    GİRİŞ FIRSATI
                  </h2>
                  <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full">
                    {categorizedSignals.go.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categorizedSignals.go.map(([symbol, symbolData]) => (
                    <SignalCard key={symbol} symbol={symbol} symbolData={symbolData} />
                  ))}
                </div>
              </section>
            )}

            {/* WAIT Signals */}
            {categorizedSignals.wait.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <h2 
                    className="text-lg font-bold text-white"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    TEYİT BEKLİYOR
                  </h2>
                  <span className="text-xs text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded-full">
                    {categorizedSignals.wait.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categorizedSignals.wait.map(([symbol, symbolData]) => (
                    <SignalCard key={symbol} symbol={symbol} symbolData={symbolData} />
                  ))}
                </div>
              </section>
            )}

            {/* ALERT Signals */}
            {categorizedSignals.alert.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <h2 
                    className="text-lg font-bold text-white"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    SEVİYEYE YAKIN
                  </h2>
                  <span className="text-xs text-orange-400 bg-orange-500/10 px-2 py-1 rounded-full">
                    {categorizedSignals.alert.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categorizedSignals.alert.map(([symbol, symbolData]) => (
                    <SignalCard key={symbol} symbol={symbol} symbolData={symbolData} />
                  ))}
                </div>
              </section>
            )}

            {/* WATCH Signals */}
            {categorizedSignals.watch.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-3 h-3 rounded-full bg-slate-500" />
                  <h2 
                    className="text-lg font-bold text-white"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    İZLENİYOR
                  </h2>
                  <span className="text-xs text-slate-400 bg-slate-500/10 px-2 py-1 rounded-full">
                    {categorizedSignals.watch.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categorizedSignals.watch.map(([symbol, symbolData]) => (
                    <SignalCard key={symbol} symbol={symbol} symbolData={symbolData} />
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {totalSignals === 0 && (
              <div className="text-center py-20">
                <Eye className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-lg text-zinc-400 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Şu an aktif sinyal yok
                </h3>
                <p className="text-sm text-zinc-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Piyasa dengeli durumda. Fırsat oluştuğunda görünecek.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            PRO MODE - Dense Table
        ═══════════════════════════════════════════════════════════════════ */}
        {viewMode === 'pro' && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-4 px-5 text-xs text-zinc-500 uppercase tracking-wider font-medium">Sembol</th>
                    <th className="text-right py-4 px-5 text-xs text-zinc-500 uppercase tracking-wider font-medium">Fiyat</th>
                    <th className="text-center py-4 px-5 text-xs text-zinc-500 uppercase tracking-wider font-medium">U/K</th>
                    <th className="text-center py-4 px-5 text-xs text-zinc-500 uppercase tracking-wider font-medium">Fonlama</th>
                    <th className="text-center py-4 px-5 text-xs text-zinc-500 uppercase tracking-wider font-medium">Kritik Seviye</th>
                    <th className="text-center py-4 px-5 text-xs text-zinc-500 uppercase tracking-wider font-medium">Uzaklık</th>
                    <th className="text-center py-4 px-5 text-xs text-zinc-500 uppercase tracking-wider font-medium">Güç</th>
                    <th className="text-center py-4 px-5 text-xs text-zinc-500 uppercase tracking-wider font-medium">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(symbols)
                    .sort((a, b) => {
                      const order = { 'ENTRY': 0, 'GO': 1, 'SETUP': 2, 'WAIT': 3, 'ALERT': 4, 'WATCH': 5 };
                      return (order[a[1]?.trade_status?.status] ?? 6) - (order[b[1]?.trade_status?.status] ?? 6);
                    })
                    .map(([symbol, symbolData]) => {
                      const tradeStatus = symbolData?.trade_status || {};
                      const keyLevel = symbolData?.key_level || {};
                      const status = tradeStatus.status || 'NEUTRAL';
                      const config = getStatusConfig(status);
                      
                      return (
                        <tr 
                          key={symbol} 
                          className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
                          data-testid={`pro-row-${symbol}`}
                        >
                          <td className="py-4 px-5">
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>{symbol}</span>
                              {tradeStatus.action && (
                                <Badge 
                                  className={`${tradeStatus.action === 'SHORT' ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'} text-[10px] border-0`}
                                >
                                  {tradeStatus.action === 'SHORT' ? 'SATIŞ' : 'ALIŞ'}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-5 text-right">
                            <span className="text-white font-mono text-sm">{formatPrice(symbolData?.current_price)}</span>
                          </td>
                          <td className="py-4 px-5 text-center">
                            <span className={`font-mono text-sm font-bold ${
                              symbolData?.long_short_ratio >= 3.0 ? 'text-red-400' :
                              symbolData?.long_short_ratio >= 2.0 ? 'text-orange-400' : 'text-zinc-300'
                            }`}>
                              {symbolData?.long_short_ratio?.toFixed(2) || '-'}
                            </span>
                          </td>
                          <td className="py-4 px-5 text-center">
                            <span className={`font-mono text-xs ${
                              symbolData?.funding_rate > 0.0005 ? 'text-red-400' :
                              symbolData?.funding_rate < -0.0005 ? 'text-emerald-400' : 'text-zinc-500'
                            }`}>
                              {symbolData?.funding_rate ? `${(symbolData.funding_rate * 100).toFixed(4)}%` : '-'}
                            </span>
                          </td>
                          <td className="py-4 px-5 text-center">
                            <div className="text-xs">
                              <span className="text-zinc-400 block">{keyLevel.name?.replace('_', ' ').replace('high', 'tepe').replace('low', 'dip').replace('24h', '24s') || '-'}</span>
                              <span className="text-amber-400 font-mono">{formatPrice(keyLevel.price)}</span>
                            </div>
                          </td>
                          <td className="py-4 px-5 text-center">
                            <span className={`font-mono text-sm ${
                              keyLevel.distance_pct <= 1.0 ? 'text-red-400 font-bold' :
                              keyLevel.distance_pct <= 2.0 ? 'text-orange-400' : 'text-zinc-400'
                            }`}>
                              {keyLevel.distance_pct ? `%${keyLevel.distance_pct.toFixed(1)}` : '-'}
                            </span>
                          </td>
                          <td className="py-4 px-5 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-12 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${getScoreColor(tradeStatus.rejection_strength || 50)}`}
                                  style={{ width: `${tradeStatus.rejection_strength || 50}%` }}
                                />
                              </div>
                              <span className="text-xs text-zinc-500 font-mono w-6">
                                {tradeStatus.rejection_strength || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-5 text-center">
                            <span 
                              className="px-3 py-1.5 rounded-full text-xs font-bold"
                              style={{ 
                                backgroundColor: `${config.color}15`,
                                color: config.color
                              }}
                            >
                              {config.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            INFO FOOTER
        ═══════════════════════════════════════════════════════════════════ */}
        <footer className="mt-12 pt-8 border-t border-zinc-800/50">
          <div className="flex flex-wrap gap-6 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span>GİR = Koşullar hazır</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-yellow-500" />
              <span>BEKLE = Teyit gerekli</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-orange-500" />
              <span>HAZIRLAN = Seviyeye yakın</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-slate-500" />
              <span>İZLE = Taranıyor</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AdminContrarianDashboard;
